import * as React from "react";
import { useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";

interface OrgContextValue {
  orgId: number | null;
  updateOrg: (newOrgId: number | null) => void;
}

const OrgContext = React.createContext<OrgContextValue | null>(null);

interface OrgProviderProps {
  children: React.ReactNode;
}

export const OrgProvider = ({ children }: OrgProviderProps) => {
  const location = useLocation();
  const history = useHistory();

  const getOrgIdFromLocation = useCallback((loc: typeof location) => {
    const param = new URLSearchParams(loc.search);
    const org = param.get("org");
    return org === null ? null : isNaN(Number(org)) ? null : parseInt(org);
  }, []);

  const updateOrg = (newOrgId: number | null) => {
    const param = new URLSearchParams(location.search);
    const oldOrgId = getOrgIdFromLocation(location);
    if (oldOrgId !== newOrgId) {
      if (!newOrgId) {
        param.delete("org");
      } else {
        param.set("org", String(newOrgId));
      }
      history.replace({
        search: param.toString(),
      });
    }
  };

  return (
    <OrgContext.Provider
      value={{ orgId: getOrgIdFromLocation(location), updateOrg }}
    >
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = (): OrgContextValue => {
  const context = React.useContext(OrgContext);
  if (!context) {
    throw new Error("useOrg must be used within an OrgProvider");
  }
  return context;
};
